// src/utils/DurationCalculator.js

/**
 * Duration Calculator Utility
 * 
 * Implements the same business logic as iOS TimeTrackingService and TimeTrackingView
 * for consistent time duration calculations across platforms.
 * 
 * Key Business Rules:
 * - Only count breaks that started after the current clock-in
 * - Work duration = Total session time - Total break time  
 * - Handle multiple clock-in/clock-out cycles per day
 * - Accumulate durations across sessions
 */

class DurationCalculator {
  
  /**
   * Calculate durations for an active session (currently clocked in)
   * Equivalent to iOS updateTimerDisplays() and calculateFinalDurations()
   * 
   * @param {Date} clockInTime - When the current session started
   * @param {Date|null} lastBreakStart - Most recent break start time
   * @param {Date|null} lastBreakEnd - Most recent break end time
   * @param {boolean} isOnBreak - Whether currently on break
   * @param {Date} currentTime - Current time (defaults to now)
   * @returns {Object} { workDuration, breakDuration, sessionDuration } in seconds
   */
  static calculateActiveSessionDurations(
    clockInTime,
    lastBreakStart = null,
    lastBreakEnd = null, 
    isOnBreak = false,
    currentTime = new Date()
  ) {
    if (!clockInTime) {
      return { workDuration: 0, breakDuration: 0, sessionDuration: 0 };
    }

    const clockIn = new Date(clockInTime);
    const now = new Date(currentTime);
    
    // Total time since clock-in
    const totalSessionDuration = (now - clockIn) / 1000; // Convert to seconds
    let totalBreakDuration = 0;

    // Calculate completed breaks (only for this session)
    if (lastBreakStart && lastBreakEnd && !isOnBreak) {
      const breakStart = new Date(lastBreakStart);
      const breakEnd = new Date(lastBreakEnd);
      
      // Only count this break if it started after the current clock-in
      if (breakStart >= clockIn) {
        const completedBreakDuration = (breakEnd - breakStart) / 1000;
        totalBreakDuration += Math.max(0, completedBreakDuration);
      }
    }

    // Calculate ongoing break if currently on break
    if (isOnBreak && lastBreakStart) {
      const breakStart = new Date(lastBreakStart);
      
      // Only count this break if it started after the current clock-in
      if (breakStart >= clockIn) {
        const currentBreakDuration = (now - breakStart) / 1000;
        totalBreakDuration += Math.max(0, currentBreakDuration);
      }
    }

    // Work duration = total session time minus all break time
    const workDuration = Math.max(0, totalSessionDuration - totalBreakDuration);

    return {
      workDuration: Math.floor(workDuration),
      breakDuration: Math.floor(totalBreakDuration),
      sessionDuration: Math.floor(totalSessionDuration)
    };
  }

  /**
   * Calculate durations for a completed time entry
   * Handles multiple clock-in/clock-out cycles per day
   * 
   * @param {Array<Date>} clockIns - Array of clock-in timestamps
   * @param {Array<Date>} clockOuts - Array of clock-out timestamps  
   * @param {Array<Date>} breakStarts - Array of break start timestamps
   * @param {Array<Date>} breakEnds - Array of break end timestamps
   * @returns {Object} { totalWorkDuration, totalBreakDuration, sessionCount, sessions }
   */
  static calculateCompletedEntryDurations(clockIns = [], clockOuts = [], breakStarts = [], breakEnds = []) {
    if (!clockIns || clockIns.length === 0) {
      return { 
        totalWorkDuration: 0, 
        totalBreakDuration: 0, 
        sessionCount: 0,
        sessions: []
      };
    }

    // Convert to Date objects and sort
    const sortedClockIns = clockIns.map(t => new Date(t)).sort((a, b) => a - b);
    const sortedClockOuts = clockOuts.map(t => new Date(t)).sort((a, b) => a - b);
    const sortedBreakStarts = breakStarts.map(t => new Date(t)).sort((a, b) => a - b);
    const sortedBreakEnds = breakEnds.map(t => new Date(t)).sort((a, b) => a - b);

    let totalWorkDuration = 0;
    let totalBreakDuration = 0;
    const sessions = [];

    // Process each clock-in/clock-out pair
    for (let i = 0; i < sortedClockIns.length; i++) {
      const clockIn = sortedClockIns[i];
      const clockOut = sortedClockOuts[i] || new Date(); // Use current time if no clock-out
      
      const sessionDuration = (clockOut - clockIn) / 1000;
      let sessionBreakDuration = 0;

      // Find breaks that occurred during this session
      const sessionBreaks = this.findSessionBreaks(clockIn, clockOut, sortedBreakStarts, sortedBreakEnds);
      
      sessionBreaks.forEach(breakPeriod => {
        sessionBreakDuration += (breakPeriod.end - breakPeriod.start) / 1000;
      });

      const sessionWorkDuration = Math.max(0, sessionDuration - sessionBreakDuration);

      sessions.push({
        clockIn,
        clockOut,
        sessionDuration: Math.floor(sessionDuration),
        workDuration: Math.floor(sessionWorkDuration),
        breakDuration: Math.floor(sessionBreakDuration),
        breaks: sessionBreaks.length
      });

      totalWorkDuration += sessionWorkDuration;
      totalBreakDuration += sessionBreakDuration;
    }

    return {
      totalWorkDuration: Math.floor(totalWorkDuration),
      totalBreakDuration: Math.floor(totalBreakDuration),
      sessionCount: sessions.length,
      sessions
    };
  }

  /**
   * Find break periods that occurred during a specific session
   * 
   * @param {Date} sessionStart - Session clock-in time
   * @param {Date} sessionEnd - Session clock-out time
   * @param {Array<Date>} breakStarts - All break start times
   * @param {Array<Date>} breakEnds - All break end times
   * @returns {Array<Object>} Array of { start, end } break periods
   */
  static findSessionBreaks(sessionStart, sessionEnd, breakStarts, breakEnds) {
    const sessionBreaks = [];

    breakStarts.forEach((breakStart, index) => {
      // Only consider breaks that started during this session
      if (breakStart >= sessionStart && breakStart <= sessionEnd) {
        const breakEnd = breakEnds[index];
        
        if (breakEnd && breakEnd > breakStart) {
          // Ensure break end doesn't exceed session end
          const effectiveBreakEnd = breakEnd > sessionEnd ? sessionEnd : breakEnd;
          
          sessionBreaks.push({
            start: breakStart,
            end: effectiveBreakEnd
          });
        }
      }
    });

    return sessionBreaks;
  }

  /**
   * Recalculate durations after time entry adjustments
   * Used when admin edits timestamps in time entries
   * 
   * @param {Object} timeEntry - Time entry data with timestamp arrays
   * @returns {Object} Updated durations
   */
  static recalculateTimeEntryDurations(timeEntry) {
    const {
      clockIns = [],
      clockOuts = [],
      breakStarts = [],
      breakEnds = []
    } = timeEntry;

    return this.calculateCompletedEntryDurations(clockIns, clockOuts, breakStarts, breakEnds);
  }

  /**
   * Validate timestamp arrays for consistency
   * 
   * @param {Array} clockIns - Clock-in timestamps
   * @param {Array} clockOuts - Clock-out timestamps
   * @param {Array} breakStarts - Break start timestamps
   * @param {Array} breakEnds - Break end timestamps
   * @returns {Object} { isValid, errors }
   */
  static validateTimestamps(clockIns = [], clockOuts = [], breakStarts = [], breakEnds = []) {
    const errors = [];

    // Basic validation
    if (clockIns.length === 0) {
      errors.push('At least one clock-in time is required');
    }

    if (clockOuts.length > clockIns.length) {
      errors.push('Cannot have more clock-outs than clock-ins');
    }

    if (breakStarts.length !== breakEnds.length) {
      errors.push('Break starts and ends must be paired');
    }

    // Chronological validation
    try {
      const sortedClockIns = clockIns.map(t => new Date(t)).sort((a, b) => a - b);
      const sortedClockOuts = clockOuts.map(t => new Date(t)).sort((a, b) => a - b);

      // Check clock-in/clock-out pairs
      for (let i = 0; i < sortedClockOuts.length; i++) {
        if (sortedClockOuts[i] <= sortedClockIns[i]) {
          errors.push(`Clock-out ${i + 1} must be after clock-in ${i + 1}`);
        }
      }

      // Check break pairs
      const sortedBreakStarts = breakStarts.map(t => new Date(t)).sort((a, b) => a - b);
      const sortedBreakEnds = breakEnds.map(t => new Date(t)).sort((a, b) => a - b);

      for (let i = 0; i < sortedBreakStarts.length; i++) {
        if (sortedBreakEnds[i] && sortedBreakEnds[i] <= sortedBreakStarts[i]) {
          errors.push(`Break end ${i + 1} must be after break start ${i + 1}`);
        }
      }

    } catch (error) {
      errors.push('Invalid timestamp format detected');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Format duration from seconds to human-readable string
   * Matches the formatting used in DashboardService
   * 
   * @param {number} seconds - Duration in seconds
   * @param {string} format - 'short' (2h 30m) or 'long' (2 hours 30 minutes)
   * @returns {string} Formatted duration
   */
  static formatDuration(seconds, format = 'short') {
    if (!seconds || seconds < 0) return format === 'short' ? '0m' : '0 minutes';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (format === 'long') {
      const parts = [];
      if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
      if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
      if (parts.length === 0 && remainingSeconds > 0) parts.push(`${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`);
      return parts.join(' ') || '0 minutes';
    }

    // Short format
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  /**
   * Format duration with seconds precision (for real-time displays)
   * 
   * @param {number} seconds - Duration in seconds
   * @returns {string} Formatted as HH:MM:SS
   */
  static formatDurationWithSeconds(seconds) {
    if (!seconds || seconds < 0) return '00:00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Calculate session statistics for admin dashboard
   * 
   * @param {Array<Object>} activeSessions - Array of active session objects
   * @returns {Object} Session statistics
   */
  static calculateSessionStats(activeSessions = []) {
    const stats = {
      totalSessions: activeSessions.length,
      workingSessions: 0,
      breakSessions: 0,
      longSessions: 0,
      averageSessionDuration: 0,
      longestSession: 0
    };

    if (activeSessions.length === 0) return stats;

    let totalDuration = 0;
    let maxDuration = 0;

    activeSessions.forEach(session => {
      if (session.onBreak) {
        stats.breakSessions++;
      } else {
        stats.workingSessions++;
      }

      const sessionDuration = session.sessionDuration || 0;
      totalDuration += sessionDuration;
      
      if (sessionDuration > maxDuration) {
        maxDuration = sessionDuration;
      }

      if (sessionDuration > 36000) { // 10 hours in seconds
        stats.longSessions++;
      }
    });

    stats.averageSessionDuration = Math.floor(totalDuration / activeSessions.length);
    stats.longestSession = maxDuration;

    return stats;
  }

  /**
   * Check if a session is considered "long" based on business rules
   * 
   * @param {number} durationInSeconds - Session duration in seconds
   * @returns {boolean} True if session is considered long
   */
  static isLongSession(durationInSeconds) {
    return durationInSeconds > 36000; // 10 hours
  }

  /**
   * Check if a break is considered "long" based on business rules
   * 
   * @param {number} durationInSeconds - Break duration in seconds  
   * @returns {boolean} True if break is considered long
   */
  static isLongBreak(durationInSeconds) {
    return durationInSeconds > 7200; // 2 hours
  }

  /**
   * Convert minutes to seconds (for compatibility with existing code)
   * 
   * @param {number} minutes - Duration in minutes
   * @returns {number} Duration in seconds
   */
  static minutesToSeconds(minutes) {
    return Math.floor(minutes * 60);
  }

  /**
   * Convert seconds to minutes (for compatibility with existing code)
   * 
   * @param {number} seconds - Duration in seconds
   * @returns {number} Duration in minutes
   */
  static secondsToMinutes(seconds) {
    return Math.floor(seconds / 60);
  }
}

export default DurationCalculator;

// ---------------------------------------------------------------------------
// Named exports for decimal-hours formatting (Hours Account feature)
// ---------------------------------------------------------------------------

/**
 * Format a signed decimal hours value for display.
 * Zero returns '+0:00 Std.'. Negative values use the Unicode minus sign
 * (U+2212) rather than a hyphen, matching German typographic convention.
 *
 * Examples:
 *   7.5   -> '+7:30 Std.'
 *  -2.25  -> '\u22122:15 Std.'
 *   0     -> '+0:00 Std.'
 *
 * @param {number} decimalHours - Signed decimal hours value
 * @returns {string} Formatted string with explicit sign and Std. unit
 */
export function formatSignedHours(decimalHours) {
  if (decimalHours == null || isNaN(decimalHours)) return '+0:00 Std.';

  const sign = decimalHours < 0 ? '\u2212' : '+';
  const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  return `${sign}${h}:${m.toString().padStart(2, '0')} Std.`;
}

/**
 * Format an unsigned decimal hours value for display.
 * Used for "Worked" and "Target" columns where values are always non-negative.
 *
 * Examples:
 *   7.5  -> '7:30 Std.'
 *   0    -> '0:00 Std.'
 *
 * @param {number} decimalHours - Decimal hours value (sign ignored)
 * @returns {string} Formatted string with Std. unit and no sign prefix
 */
export function formatHours(decimalHours) {
  if (decimalHours == null || isNaN(decimalHours)) return '0:00 Std.';

  const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  return `${h}:${m.toString().padStart(2, '0')} Std.`;
}